import { type Context, createContext, useCallback, useContext } from 'react';

import FeatureGates, { type GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client';
// @ts-ignore - this is not a valid package entry point and cannot be resolved when using a modern Typescript 'moduleResolution' setting
import type { ExperimentCohortVariationControl } from '@atlassian/touchpoint-commons-event-analytics/src/common/types';

import { useFeatureExposedAnalytics } from '../analytics/feature-exposed-analytics';
import type { ExperimentCohort as ExperimentCohortTreatment } from '../types';

type ExperimentAttributes = {
	[key: string]: any;
};

export type ExperimentDictionary = {
	[key: string]: {
		cohort: ExperimentCohortVariationControl;
		timestamp: number;
	} & ExperimentAttributes;
};

export class ExperimentContextClass {
	private experimentDictionary: ExperimentDictionary = {};

	constructor(context?: ExperimentDictionary) {
		this.experimentDictionary = context ?? {};
	}

	experimentExposed(
		experimentName: string,
		experimentVariant: ExperimentCohortVariationControl,
		extraAttributes: ExperimentAttributes,
	): void {
		// If the experiment is already in the dictionary (i.e. been exposed before),
		// we don't want to overwrite the timestamp of first exposure.

		if (Object.keys(this.experimentDictionary).includes(experimentName)) {
			return;
		}

		this.experimentDictionary[experimentName] = {
			cohort: experimentVariant,
			timestamp: Date.now(),
			...extraAttributes,
		};
	}

	experiments(): ExperimentDictionary {
		return this.experimentDictionary;
	}
}

export const ExperimentContext: Context<ExperimentContextClass | null> =
	createContext<ExperimentContextClass | null>(null);

export function useExperimentContext(): ExperimentContextClass {
	const context = useContext(ExperimentContext);

	if (!context) {
		throw new Error('Missing context, please ensure this is used within ExperimentContext');
	}

	return context;
}

// Helper functions to transform experiment cohorts
export const booleanCohortTransformFn = (
	parameterValue: boolean,
): ExperimentCohortVariationControl => (parameterValue ? 'variation' : 'control');

export const treatmentCohortTransformFn = (
	cohort: ExperimentCohortTreatment,
): ExperimentCohortVariationControl => {
	if (cohort === 'treatment') {
		return 'variation';
	} else if (cohort === 'control') {
		return 'control';
	} else {
		return 'not-enrolled';
	}
};

type GetExperimentValueParams = {
	experimentContextAttributes?: ExperimentAttributes;
	statsigParams: {
		defaultValue: ExperimentCohortVariationControl;
		experimentName: string;
		options?: GetExperimentValueOptions<ExperimentCohortVariationControl>;
		parameterName: string;
	};
	trackEventParameters?: {
		trackEventAttributes?: Record<string, unknown>;
		trackEventGrowthInitiative: string;
	};
};

type GetExperimentValueWithTransformParams<T> = {
	experimentContextAttributes?: ExperimentAttributes;
	statsigParams: {
		defaultValue: T;
		experimentName: string;
		options?: GetExperimentValueOptions<T>;
		parameterName: string;
	};
	trackEventParameters?: {
		trackEventAttributes?: Record<string, unknown>;
		trackEventGrowthInitiative: string;
	};
	transformCohortFunc: (cohort: T) => ExperimentCohortVariationControl;
};

/**
 * A helper hook which wraps the call to FeatureGates.getExperimentValue.
 * Returns a function that retrieves experiment values.
 *
 * If fireExperimentExposure is true, it will also update the experiment context.
 * By default, fireExperimentExposure is true if not provided.
 *
 * If trackEventParameters is provided, it will also fire an experiment exposed track event.
 */
export const useGetExperimentValue = (): {
	getExperimentValue: (props: GetExperimentValueParams) => ExperimentCohortVariationControl;
	getExperimentValueWithTransform: <T>(props: GetExperimentValueWithTransformParams<T>) => T;
} => {
	const experimentContext = useExperimentContext();
	const fireExperimentExposureEvent = useFeatureExposedAnalytics();

	const getExperimentValue: (props: GetExperimentValueParams) => ExperimentCohortVariationControl =
		useCallback(
			({
				statsigParams: { experimentName, parameterName, defaultValue, options },
				trackEventParameters,
				experimentContextAttributes = {},
			}): ExperimentCohortVariationControl => {
				const cohort = options
					? FeatureGates.getExperimentValue<ExperimentCohortVariationControl>(
							experimentName,
							parameterName,
							defaultValue,
							options,
						)
					: FeatureGates.getExperimentValue<ExperimentCohortVariationControl>(
							experimentName,
							parameterName,
							defaultValue,
						);

				// By default, fireExperimentExposure is true but we need to explicitly
				// check for that to ensure we fire our context update correctly.
				if (options?.fireExperimentExposure !== false) {
					experimentContext.experimentExposed(experimentName, cohort, experimentContextAttributes);

					if (trackEventParameters) {
						fireExperimentExposureEvent(
							experimentName,
							cohort,
							trackEventParameters?.trackEventGrowthInitiative,
							trackEventParameters?.trackEventAttributes,
						);
					}
				}
				return cohort;
			},
			[experimentContext, fireExperimentExposureEvent],
		);

	/**
	 * @deprecated Use getExperimentValue instead with the correct experiment cohort values.
	 * We are moving towards consistent cohort values of 'control', 'variation' and 'not-enrolled'.
	 */
	const getExperimentValueWithTransform: <T>(props: GetExperimentValueWithTransformParams<T>) => T =
		useCallback(
			({
				statsigParams: { experimentName, parameterName, defaultValue, options },
				transformCohortFunc,
				trackEventParameters,
				experimentContextAttributes = {},
			}) => {
				const cohort = options
					? FeatureGates.getExperimentValue(experimentName, parameterName, defaultValue, options)
					: FeatureGates.getExperimentValue(experimentName, parameterName, defaultValue);

				// By default, fireExperimentExposure is true but we need to explicitly
				// check for that to ensure we fire our context update correctly.
				if (options?.fireExperimentExposure !== false) {
					experimentContext.experimentExposed(
						experimentName,
						transformCohortFunc(cohort),
						experimentContextAttributes,
					);

					if (trackEventParameters && typeof cohort === 'string') {
						fireExperimentExposureEvent(
							experimentName,
							cohort,
							trackEventParameters?.trackEventGrowthInitiative,
							trackEventParameters?.trackEventAttributes,
						);
					}
				}
				return cohort;
			},
			[experimentContext, fireExperimentExposureEvent],
		);

	return { getExperimentValue, getExperimentValueWithTransform };
};
